import { ContractTransaction, ethers } from 'ethers';
import { BigNumber } from '@ethersproject/bignumber';
import { ERC20_ABI, createSiloLensContract, getGasOverride, getWrappedNativeTokenAddress } from '@silo/contract-utils';
import { SiloRouterV2, SiloRouterV2__factory } from '@silo/typechain';
import { AddressZero, MaxUint256 } from '@ethersproject/constants';
import { getBorrowAmount } from 'app/functions/getBorrowAmount';
import { getProviderOrSigner, useSiloWeb3 } from '@silo/web3';
import { useMemo } from 'react';
import { isNull, mapNullables } from '@silo/utils';
import { useThisDecoratedSilo } from '../features/silo/silo';
import { ChecksumAddress, getChecksumAddressOrThrow, isSupportedChainId } from '@silo/types';

enum RouterActionType {
  Deposit = 0,
  Withdraw = 1,
  Borrow = 2,
  Repay = 3,
  Wrap = 4,
  Unwrap = 5,
}

type SiloRouterAction = {
  actionType: RouterActionType;
  silo: ChecksumAddress;
  asset: ChecksumAddress;
  amount: BigNumber;
  collateralOnly: boolean;
};

export interface Withdrawal {
  siloAddress: ChecksumAddress;
  assetAddress: ChecksumAddress;
  amount: BigNumber;
  isCollateralOnly?: boolean;
  isFullAmount: boolean;
}

const useSiloRouterContract = (routerAddress: ChecksumAddress): SiloRouterV2 | null => {
  const { account, signingProvider } = useSiloWeb3();

  return useMemo(
    () =>
      mapNullables({ account, signingProvider }, ({ account, signingProvider }) => {
        return <SiloRouterV2>(
          new ethers.Contract(routerAddress, SiloRouterV2__factory.abi, getProviderOrSigner(signingProvider, account))
        );
      }),
    [routerAddress, account, signingProvider]
  );
};

const APPROVAL_GAS_BUFFER = 10;

const useSiloRouter = () => {
  const { routerAddress } = useThisDecoratedSilo();
  const siloRouterContract = useSiloRouterContract(routerAddress);
  const { account, signingProvider, clientProvider, chainId, protocolInstance } = useSiloWeb3();

  const approveRouterAssetSpend = async (
    siloAssetAddress: ChecksumAddress,
    amountToApprove: BigNumber = MaxUint256
  ) => {
    if (!signingProvider || !clientProvider || !account) {
      throw new Error('provider or account not defined');
    }

    if (isNull(siloRouterContract)) {
      throw new Error('SiloRouter is null');
    }

    const erc20Contract = new ethers.Contract(
      siloAssetAddress,
      ERC20_ABI,
      getProviderOrSigner(signingProvider, account)
    );

    const tx = await erc20Contract.populateTransaction.approve(siloRouterContract.address, amountToApprove);
    const gasOverride = await getGasOverride(clientProvider, tx, APPROVAL_GAS_BUFFER);

    return (await erc20Contract.approve(
      siloRouterContract.address,
      amountToApprove,
      gasOverride
    )) as ContractTransaction;
  };

  const wrappedActionExecution = async (routerAction: SiloRouterAction, overrides?: ethers.PayableOverrides) => {
    if (!routerAction) throw new Error('no router action provided for wrapped execution');

    if (!signingProvider || !clientProvider) {
      throw new Error('provider is not defined');
    }

    if (isNull(siloRouterContract)) {
      throw new Error('SiloRouter is null');
    }

    if (!isSupportedChainId(chainId)) {
      throw new Error(`Unsupported chainId: ${chainId}`);
    }

    if (routerAction.asset === getWrappedNativeTokenAddress(chainId)) {
      console.log('[silorouter] using wrapped native for action, will send eth');
      const payableOverride = { value: routerAction.amount, ...overrides };

      const tx = await siloRouterContract.populateTransaction.execute([routerAction], payableOverride);
      const gasOverride = await getGasOverride(clientProvider, tx);

      return await siloRouterContract.execute([routerAction], { ...payableOverride, ...gasOverride });
    } else {
      const tx = await siloRouterContract.populateTransaction.execute([routerAction]);
      const gasOverride = await getGasOverride(clientProvider, tx);

      return await siloRouterContract.execute([routerAction], gasOverride);
    }
  };

  const routerDeposit = async (
    siloAddress: ChecksumAddress,
    assetAddress: ChecksumAddress,
    amount: BigNumber,
    collateralOnly: boolean,
    isCurveLpAsset: boolean
  ) => {
    if (isCurveLpAsset) {
      if (!signingProvider || !clientProvider) {
        throw new Error('Undefined provider');
      }

      if (isNull(siloRouterContract)) {
        throw new Error('SiloRouter is null');
      }

      const wrapDepositActions: SiloRouterAction[] = [
        {
          actionType: RouterActionType.Wrap,
          silo: getChecksumAddressOrThrow(AddressZero),
          asset: assetAddress, //note: this is the convex wrapper (user has curve lps)
          amount: amount,
          collateralOnly: false,
        },
        {
          actionType: RouterActionType.Deposit,
          silo: siloAddress,
          asset: assetAddress,
          amount: amount,
          collateralOnly: true, //note: we are doing collateral only, only
        },
      ];

      const tx = await siloRouterContract.populateTransaction.execute(wrapDepositActions);
      const gasOverride = await getGasOverride(clientProvider, tx);

      return await siloRouterContract.execute(wrapDepositActions, gasOverride);
    }

    const routerAction: SiloRouterAction = {
      actionType: RouterActionType.Deposit,
      silo: siloAddress,
      asset: assetAddress,
      amount,
      collateralOnly,
    };

    return await wrappedActionExecution(routerAction);
  };

  const routerBorrow = async (siloAddress: ChecksumAddress, assetAddress: ChecksumAddress, amount: BigNumber) => {
    if (!signingProvider || !clientProvider) {
      throw new Error('provider is not defined');
    }

    if (isNull(siloRouterContract)) {
      throw new Error('SiloRouter is null');
    }

    const routerAction: SiloRouterAction = {
      actionType: RouterActionType.Borrow,
      silo: siloAddress,
      asset: assetAddress,
      amount,
      collateralOnly: false,
    };

    const tx = await siloRouterContract.populateTransaction.execute([routerAction]);
    const gasOverride = await getGasOverride(clientProvider, tx);

    return await siloRouterContract.execute([routerAction], gasOverride);
  };

  const routerWithdraw = async (...withdrawals: Withdrawal[]) => {
    if (!signingProvider || !clientProvider) {
      throw new Error('provider is not defined');
    }

    if (isNull(siloRouterContract)) {
      throw new Error('SiloRouter is null');
    }

    const routerActions: SiloRouterAction[] = withdrawals.map(
      ({ siloAddress, assetAddress, amount, isFullAmount, isCollateralOnly = false }) => ({
        actionType: RouterActionType.Withdraw,
        silo: siloAddress,
        asset: assetAddress,
        amount: isFullAmount ? MaxUint256 : amount,
        collateralOnly: isCollateralOnly,
      })
    );

    const tx = await siloRouterContract.populateTransaction.execute(routerActions);
    const gasOverride = await getGasOverride(clientProvider, tx);

    return await siloRouterContract.execute(routerActions, gasOverride);
  };

  const routerWithdrawConvexWrapper = async ({ assetAddress, siloAddress, amount, isFullAmount }: Withdrawal) => {
    if (!signingProvider || !clientProvider) {
      throw new Error('provider is not defined');
    }

    if (isNull(siloRouterContract)) {
      throw new Error('SiloRouter is null');
    }

    const wrapDepositActions: SiloRouterAction[] = [
      {
        actionType: RouterActionType.Withdraw,
        silo: siloAddress,
        asset: assetAddress,
        amount: isFullAmount ? MaxUint256 : amount,
        collateralOnly: true, //note: we are doing collateral only, only
      },
      {
        actionType: RouterActionType.Unwrap,
        silo: getChecksumAddressOrThrow(AddressZero),
        asset: assetAddress, //note: this is the convex wrapper (user has curve lps)
        amount: amount,
        collateralOnly: false,
      },
    ];

    const tx = await siloRouterContract.populateTransaction.execute(wrapDepositActions);
    const gasOverride = await getGasOverride(clientProvider, tx);

    return await siloRouterContract.execute(wrapDepositActions, gasOverride);
  };

  const routerRepay = async (
    siloAddress: ChecksumAddress,
    assetAddress: ChecksumAddress,
    amount: BigNumber,
    overrides?: ethers.PayableOverrides
  ) => {
    const routerAction: SiloRouterAction = {
      actionType: RouterActionType.Repay,
      silo: siloAddress,
      asset: assetAddress,
      amount,
      collateralOnly: false,
    };

    return await wrappedActionExecution(routerAction, overrides);
  };

  const routerRepayAll = async (
    siloAddress: ChecksumAddress,
    assetAddress: ChecksumAddress,
    userAddress: ChecksumAddress
  ) => {
    if (!signingProvider || !chainId || !clientProvider || !protocolInstance) {
      throw new Error('provider or chainId is not defined');
    }

    if (!isSupportedChainId(chainId)) {
      throw new Error(`Unsupported chainId: ${chainId}`);
    }

    if (isNull(siloRouterContract)) {
      throw new Error('SiloRouter is null');
    }

    const routerAction: SiloRouterAction = {
      actionType: RouterActionType.Repay,
      silo: siloAddress,
      asset: assetAddress,
      amount: MaxUint256,
      collateralOnly: false,
    };

    if (routerAction.asset === getWrappedNativeTokenAddress(chainId)) {
      const payableValue = await getBorrowAmount({
        clientProvider: clientProvider,
        siloLens: createSiloLensContract({ protocolInstance, provider: clientProvider }),
        siloAddress,
        assetAddress: getWrappedNativeTokenAddress(chainId),
        user: userAddress,
        futureDuration: { minutes: 2 },
      });
      const payableOverride = { value: payableValue };

      const tx = await siloRouterContract.populateTransaction.execute([routerAction], payableOverride);
      const gasOverride = await getGasOverride(clientProvider, tx);

      return await siloRouterContract.execute([routerAction], { ...payableOverride, ...gasOverride });
    } else {
      const tx = await siloRouterContract.populateTransaction.execute([routerAction]);
      const gasOverride = await getGasOverride(clientProvider, tx);

      return await siloRouterContract.execute([routerAction], gasOverride);
    }
  };

  return {
    approveRouterAssetSpend,
    siloRouterContract,
    routerDeposit,
    routerBorrow,
    routerRepay,
    getBorrowAmount,
    routerRepayAll,
    routerWithdraw,
    routerWithdrawConvexWrapper,
  };
};

export default useSiloRouter;
